import { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FolderKanban, UserCog, ScrollText, BookOpen, LogOut, ShieldCheck, Loader2, AlertTriangle, Camera, MessageCircle, BadgeCheck, Megaphone, Flag, BarChart3, ShieldAlert, Crown, KeyRound, Coins, Languages, Smartphone, Sparkles, Trophy, HandHeart, FileText, Search, Store, Inbox, Activity, ListChecks, UserPlus, Rocket, Bot, Home,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { roleLabel } from "@/data/roles";
import { PageGuide } from "@/components/PageGuide";
import { DemoTour } from "@/components/DemoTour";

export const avatarUrl = (picture) =>
  !picture ? null : picture.startsWith("http") ? picture : `${API}/files/${picture}`;

const PORTAL_ROLES = ["super_admin", "agent", "support", "manager", "ceo", "auditor"];
const EXEC = ["manager", "ceo"];
const EXEC_VIEW = ["manager", "ceo", "auditor"];

const NAV = [
  { to: "/portal/exec", label: "Executive", icon: Crown, roles: EXEC_VIEW },
  { to: "/portal/business-plan", label: "Business Plan", icon: FileText, roles: ["ceo", "super_admin"] },
  { to: "/portal/property-plan", label: "Property Plan", icon: Home, roles: ["ceo", "super_admin"] },
  { to: "/portal/team", label: "Team", icon: UserCog, roles: EXEC_VIEW },
  { to: "/portal/referral-leaderboard", label: "Referral leaders", icon: Trophy, roles: EXEC_VIEW },
  { to: "/portal/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: PORTAL_ROLES },
  { to: "/portal/onboarding", label: "Getting started", icon: Sparkles, roles: ["agent", "support"] },
  { to: "/portal/field", label: "Field mode", icon: Smartphone, roles: ["agent", "super_admin"] },
  { to: "/portal/clients", label: "Clients", icon: Users, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/proposals", label: "Proposals", icon: FileText, roles: ["super_admin", "agent", "manager", "ceo", "auditor"] },
  { to: "/portal/cases", label: "Cases", icon: FolderKanban, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/resources", label: "Resources", icon: BookOpen, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/voices", label: "Your Voice", icon: MessageCircle, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/enquiries", label: "Enquiries", icon: Inbox, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/copilot", label: "Copilot Studio", icon: Sparkles, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/reception", label: "Reception Logs", icon: MessageCircle, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/wallet-waitlist", label: "Wallet Waitlist", icon: Coins, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/launch-rsvps", label: "Launch RSVPs", icon: MessageCircle, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/launch-checklist", label: "Launch Checklist", icon: ListChecks, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/launch-recap", label: "Launch Recap", icon: BarChart3, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/signup-helper", label: "Sign-up Helper", icon: UserPlus, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/discussions", label: "Discussions", icon: MessageCircle, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/happenings", label: "Happenings", icon: Megaphone, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/market-reports", label: "Market Reports", icon: Flag, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/live-moderation", label: "Live Moderation", icon: Flag, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/market-search", label: "Market Search", icon: Search, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/maker-verifications", label: "Maker Verifications", icon: BadgeCheck, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/local-businesses", label: "Local Businesses", icon: Store, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/ads", label: "Pula Ads", icon: Megaphone, roles: ["super_admin", "manager", "ceo"] },
  { to: "/portal/ambassadors", label: "Creators", icon: HandHeart, roles: ["super_admin", "agent", "manager", "ceo"] },
  { to: "/portal/student-reports", label: "Student Reports", icon: Flag, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/student-disputes", label: "Student Disputes", icon: ShieldAlert, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/jobs-moderation", label: "Jobs Moderation", icon: ShieldAlert, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/lens-moderation", label: "Lens Moderation", icon: ShieldCheck, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/legacy-fund", label: "Legacy Fund", icon: HandHeart, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/blog", label: "Pula Stories", icon: BookOpen, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/analytics", label: "Analytics", icon: BarChart3, roles: ["super_admin", "manager", "ceo", "auditor"] },
  { to: "/portal/marketing", label: "Marketing", icon: Megaphone, roles: ["super_admin", "manager", "ceo"] },
  { to: "/portal/broadcast-channels", label: "SMS & WhatsApp", icon: MessageCircle, roles: ["super_admin", "manager", "ceo"] },
  { to: "/portal/reengage", label: "Re-engage", icon: HandHeart, roles: ["super_admin", "ceo", "manager", "agent", "support"] },
  { to: "/portal/lead-studio", label: "Lead Studio", icon: Rocket, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/ai-team", label: "AI Team", icon: Bot, roles: ["super_admin", "agent", "support", "manager", "ceo"] },
  { to: "/portal/community-mods", label: "Community Mods", icon: ShieldCheck, roles: ["super_admin", "ceo"] },
  { to: "/portal/verifications", label: "Verifications", icon: BadgeCheck, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/safety-concerns", label: "Safety Concerns", icon: ShieldAlert, roles: ["super_admin", "agent", "support"] },
  { to: "/portal/staff", label: "Staff", icon: UserCog, roles: ["super_admin"] },
  { to: "/portal/staff-activity", label: "Staff Activity", icon: Activity, roles: ["ceo", "manager", "super_admin"] },
  { to: "/portal/audit", label: "Audit log", icon: ScrollText, roles: ["super_admin", "manager", "ceo", "auditor"] },
  { to: "/portal/security", label: "Security", icon: KeyRound, roles: PORTAL_ROLES },
  { to: "/portal/rewards", label: "Rewards", icon: Coins, roles: PORTAL_ROLES },
  { to: "/portal/translate", label: "Voice & Translate", icon: Languages, roles: PORTAL_ROLES },
  { to: "/portal/safety-translations", label: "Safety Translations", icon: Languages, roles: ["super_admin", "ceo", "manager"] },
];

const AGREEMENT = [
  "I will always put clients first and treat everyone with equal respect.",
  "I will never collect payments or handle money — all payments go to official accounts only.",
  "I will never ask about a client's immigration or visa status abroad — only citizenship for land ownership.",
  "I will keep all client data private and secure, and only access what I need.",
];

export default function PortalLayout() {
  const { user, loading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [accepting, setAccepting] = useState(false);
  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [modCount, setModCount] = useState(0);
  const [corrCount, setCorrCount] = useState(0);

  useEffect(() => {
    if (user && ["super_admin", "agent", "support"].includes(user.role)) {
      api.get("/moderation/pending-count").then(({ data }) => setModCount(data.count || 0)).catch(() => {});
    }
    if (user && ["super_admin", "ceo", "manager"].includes(user.role)) {
      api.get("/community/admin/translation-corrections/count").then(({ data }) => setCorrCount(data.count || 0)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!user || !PORTAL_ROLES.includes(user.role)) return;
    const ping = () => api.post("/security/heartbeat").catch(() => {});
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [user]);

  // Idle auto-logout: sign staff out after 30 min of no interaction (1-min warning).
  useEffect(() => {
    if (!user || !PORTAL_ROLES.includes(user.role)) return;
    const IDLE_MS = 30 * 60 * 1000;
    const WARN_MS = IDLE_MS - 60 * 1000;
    let logoutTimer, warnTimer;
    const reset = () => {
      clearTimeout(logoutTimer);
      clearTimeout(warnTimer);
      warnTimer = setTimeout(() => toast.warning("You'll be signed out in 1 minute due to inactivity. Move your mouse to stay signed in."), WARN_MS);
      logoutTimer = setTimeout(() => { toast.info("Signed out due to inactivity."); logout(); navigate("/portal"); }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(logoutTimer);
      clearTimeout(warnTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout, navigate]);

  useEffect(() => {
    if (!loading && (!user || !PORTAL_ROLES.includes(user.role))) {
      navigate("/portal");
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-pula-bg"><Loader2 className="w-8 h-8 animate-spin text-pula-green" /></div>;
  }

  if (!PORTAL_ROLES.includes(user.role)) return null;

  const accept = async () => {
    setAccepting(true);
    try { await api.post("/portal/accept-agreement"); await refresh(); } finally { setAccepting(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload photo.");
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  if (!user.agreement_accepted) {
    return (
      <div className="min-h-screen grid place-items-center bg-pula-bg px-4">
        <div className="pula-card p-8 max-w-lg" data-testid="agreement-screen">
          <ShieldCheck className="w-10 h-10 text-pula-green mb-3" />
          <h1 className="font-heading font-extrabold text-2xl text-pula-ink">Agent Agreement</h1>
          <p className="font-body text-pula-inksoft mt-2">Please read and accept before accessing the portal.</p>
          <ul className="mt-5 space-y-3">
            {AGREEMENT.map((a, i) => (
              <li key={i} className="flex items-start gap-2 font-body text-sm text-pula-ink"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-pula-green shrink-0" /> {a}</li>
            ))}
          </ul>
          <Button data-testid="accept-agreement" onClick={accept} disabled={accepting} className="mt-6 w-full rounded-full bg-pula-green hover:bg-pula-greenhover text-white">
            {accepting ? "Please wait…" : "I accept the Agent Agreement"}
          </Button>
        </div>
      </div>
    );
  }

  const nav = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-pula-bg2">
      <aside className="hidden md:flex flex-col w-64 bg-pula-ink text-pula-bg p-4 shrink-0">
        <div className="flex items-center gap-2 px-2 py-3 mb-4">
          <span className="w-8 h-8 rounded-lg bg-pula-green grid place-items-center text-white font-heading font-extrabold">P</span>
          <div>
            <p className="font-heading font-bold text-sm leading-tight">Agent Portal</p>
            <p className="text-xs text-pula-bg/60">Pula & Mavula</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`portal-nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-body font-medium transition-colors ${isActive ? "bg-pula-green text-white" : "text-pula-bg/80 hover:bg-white/10"}`}>
              <n.icon className="w-4 h-4" /> <span className="flex-1">{n.label}</span>
              {n.to === "/portal/lens-moderation" && modCount > 0 && (
                <span data-testid="lens-mod-badge" className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-pula-accent text-white text-[11px] font-bold grid place-items-center">{modCount}</span>
              )}
              {n.to === "/portal/safety-translations" && corrCount > 0 && (
                <span data-testid="corrections-badge" className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-pula-accent text-white text-[11px] font-bold grid place-items-center">{corrCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-3 mt-3">
          <div className="flex items-center gap-3 px-3 mb-3">
            <button data-testid="portal-avatar-upload" onClick={() => avatarInputRef.current?.click()}
              className="relative w-11 h-11 rounded-full overflow-hidden bg-pula-green/30 grid place-items-center shrink-0 group">
              {avatarUploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              ) : avatarUrl(user.picture) ? (
                <img src={avatarUrl(user.picture)} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-heading font-bold text-white">{user.name?.[0] || "?"}</span>
              )}
              <span className="absolute inset-0 bg-pula-ink/50 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-4 h-4 text-white" />
              </span>
            </button>
            <input ref={avatarInputRef} data-testid="portal-avatar-input" type="file" accept="image/*" hidden onChange={uploadAvatar} />
            <div className="min-w-0">
              <p className="text-xs text-pula-bg/80 font-body truncate">{user.name}</p>
              <p className="text-xs text-pula-green font-body capitalize">{roleLabel(user.role)}</p>
            </div>
          </div>
          <a data-testid="portal-help" href="https://wa.me/447424311882" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-pula-bg/80 hover:text-white transition-colors">
            <MessageCircle className="w-4 h-4" /> Get help (WhatsApp)
          </a>
          <button data-testid="portal-logout" onClick={() => { logout(); navigate("/portal"); }} className="flex items-center gap-2 px-3 py-2 text-sm text-pula-bg/80 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden bg-pula-ink text-pula-bg p-3 flex items-center gap-2 overflow-x-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-pula-green text-white" : "bg-white/10"}`}>{n.label}</NavLink>
          ))}
          <button onClick={() => { logout(); navigate("/portal"); }} className="px-3 py-1.5 rounded-full text-xs bg-white/10">Log out</button>
        </div>
        <div className="bg-pula-highlight/20 border-b border-pula-highlight/40 px-6 py-2 flex items-center gap-2 text-xs text-pula-olive font-body" data-testid="portal-immigration-reminder">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Reminder: We do not ask about immigration status abroad — only citizenship for land ownership.
        </div>
        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <PageGuide />
      <DemoTour variant="portal" />
    </div>
  );
}
